import { getUserPermissions, hasPermission } from "utils/permissionUtils";
import { P } from "const/permissions";

/**
 * Structured notification navigation: each notification carries a `type` (semantic event) and a
 * `metadata` payload. Routing is a pure lookup `navRegistry[type](item)` reading `item.metadata` —
 * never the message text. Adding a new event = one backend enum value + one registry entry here.
 */

function canOpenDirectAccountEditor() {
  const { userDetail } = getUserPermissions();
  if (userDetail?.type === "SUPER_ADMIN" || userDetail?.type === "ACCOUNT") return true;
  return hasPermission(P.ACCOUNTS.READ);
}

const clientView = (clientId, tab) =>
  clientId
    ? { path: `/admin/client/view/${clientId}`, state: tab != null ? { tab } : undefined }
    : null;

const clientAccountsTab = (clientId) =>
  clientId ? { path: `/admin/client/view/${clientId}`, state: { tab: "accounts" } } : null;

const taskView = (i) =>
  i.metadata?.taskId
    ? { path: `/admin/tasks/view/${i.metadata.taskId}` }
    : { path: "/admin/tasks" };

/**
 * Deep-link into the account editor for a client, focusing an admission/term and optionally opening
 * the verification modal or the term-notes modal. Falls back to the read-only accounts tab when the
 * recipient lacks account access.
 */
function directAccountEditor(i, focus = {}) {
  const m = i.metadata || {};
  if (!m.clientId) return null;
  if (!canOpenDirectAccountEditor()) return clientAccountsTab(m.clientId);
  return {
    path: "/admin/account/add",
    state: {
      item: { clientData: { id: m.clientId } },
      accountFocus: {
        admissionId: m.admissionId ?? null,
        termNumber: m.termNumber ?? null,
        moduleType: m.moduleType ?? null,
        moduleId: m.moduleId ?? null,
        paymentId: m.paymentId ?? null,
        ...focus,
      },
    },
  };
}

/**
 * A partner's admin detail screen. The route is /admin/admin-referral/partners/:id — the Referral
 * module's own list lives at /admin/referral, so the two are not interchangeable.
 */
const partnerDetail = (partnerId, state) =>
  partnerId
    ? {
        path: `/admin/admin-referral/partners/${partnerId}`,
        state: state ?? undefined,
      }
    : { path: "/admin/admin-referral" };

const officeTicket = (ticketId) =>
  ticketId
    ? { path: `/admin/admin-referral/support/${ticketId}` }
    : { path: "/admin/admin-referral/support" };

// Client-view tab indices by module.
const MODULE_TAB = { visa: 5, insurance: 3, client: 3, student: 4, reminder: 3 };

// Tab index of the "Documents" tab in the admission edit page (KotharDashboard/src/views/admin/Admissions/AddAdmissions/index.js TABS).
const ADMISSION_DOCUMENTS_TAB = 1;

export const navRegistry = {
  PAYMENT_NEEDS_VERIFICATION: (i) => directAccountEditor(i, { openVerificationModal: true }),
  PAYMENT_VERIFIED: (i) => directAccountEditor(i, {}),
  PAYMENT_REJECTED: (i) => directAccountEditor(i, {}),
  PAYMENT_WITHDRAWN: (i) => directAccountEditor(i, {}),
  ACCOUNT_ENTRY_NEEDS_VERIFICATION: (i) => directAccountEditor(i, { openVerificationModal: true }),
  ACCOUNT_ENTRY_VERIFIED: (i) => directAccountEditor(i, {}),
  ACCOUNT_ENTRY_REJECTED: (i) => clientAccountsTab(i.metadata?.clientId),
  TERM_NOTE_MENTION: (i) => directAccountEditor(i, { openTermNotes: true }),
  ADMISSION_NOTE_MENTION: (i) =>
    i.metadata?.admissionId
      ? { path: `/admin/admission/edit/${i.metadata.admissionId}`, state: { tab: 4 } }
      : clientAccountsTab(i.metadata?.clientId),
  // A signed college document is filed into the linked admission's Documents tab when there is
  // one; otherwise there is no admission to file it into (see CollegeDocumentCompletionListener),
  // so the closest useful destination is the college's own document library, where it was sent
  // from ("Sent Documents" is not a separate route today).
  COLLEGE_DOCUMENT_SIGNED: (i) =>
    i.metadata?.admissionId
      ? {
          path: `/admin/admission/edit/${i.metadata.admissionId}`,
          state: { tab: ADMISSION_DOCUMENTS_TAB },
        }
      : i.metadata?.universityId
      ? {
          // Task 20 adds this route and its Sent documents tab; a send with no admission linked
          // is recorded there rather than against an admission.
          path: `/admin/university/${i.metadata.universityId}/documents/sent`,
          state: { packetId: i.metadata.packetId },
        }
      : clientView(i.metadata?.clientId, 4),
  // A signed visa document belongs to the visa application it was sent from, so open that
  // application straight onto its Documents tab (the tab is selected by query param, see
  // KotharDashboard/src/views/admin/Visa/AddVisa.js TAB_QUERY_PARAM_TO_INDEX).
  VISA_DOCUMENT_SIGNED: (i) =>
    i.metadata?.visaApplicationId
      ? { path: `/admin/visa/edit/${i.metadata.visaApplicationId}?tab=documents` }
      : clientView(i.metadata?.clientId, MODULE_TAB.visa),
  MODULE_NOTE_MENTION: (i) => clientView(i.metadata?.clientId, MODULE_TAB[i.metadata?.module] ?? 3),
  TASK_ASSIGNED: taskView,
  TASK_UPDATED: taskView,
  TASK_COMMENT: taskView,
  TASK_ADMIN_NOTIFY: taskView,
  FOLLOW_UP: (i) =>
    i.metadata?.clientId && i.metadata?.followUpId
      ? { path: `/admin/follow-up/view/${i.metadata.clientId}/${i.metadata.followUpId}` }
      : { path: "/admin/follow-up" },
  REMINDER: (i) => clientView(i.metadata?.clientId, 3),
  VISA_EXPIRY: (i) => clientView(i.metadata?.clientId),
  DOCUMENT_EXPIRY: (i) => clientView(i.metadata?.clientId),
  CENSUS_DATE: (i) => directAccountEditor(i, {}),
  PAYMENT_DUE: (i) => directAccountEditor(i, {}),
  ADMISSION_FEE_REMINDER: (i) => directAccountEditor(i, {}),
  // The standalone invoice page needs invoice:invoice:read. A client-invoice creator who only
  // holds invoice:client:read would bounce off it, so send them to the client's Invoices tab —
  // the same place they raised the invoice from.
  INVOICE_DUE: (i) => {
    const m = i.metadata || {};
    const invoicePage = m.invoiceId ? { path: `/admin/invoice/view/${m.invoiceId}` } : null;
    if (hasPermission(P.INVOICE.READ)) return invoicePage;
    return clientView(m.clientId, "invoices") || invoicePage;
  },
  // Client-tab invoice sent for activation or sent payments for verification: the
  // approve/verify queues live on the Invoice page.
  INVOICE_NEEDS_VERIFICATION: () => ({ path: "/admin/invoice" }),
  // Activation/payment approved or rejected: back to the client's Invoices tab.
  INVOICE_VERIFIED: (i) => clientView(i.metadata?.clientId, "invoices"),
  INVOICE_REJECTED: (i) => clientView(i.metadata?.clientId, "invoices"),
  BIRTHDAY: (i) => clientView(i.metadata?.clientId, 3),
  VISA_UPDATE: (i) => clientView(i.metadata?.clientId, 5),
  INSURANCE_UPDATE: (i) => clientView(i.metadata?.clientId, 3),
  INSURANCE_EXPIRY: (i) => clientView(i.metadata?.clientId, MODULE_TAB.insurance),
  RPL_UPDATE: (i) =>
    i.metadata?.clientId ? { path: `/admin/rpl-client/edit/${i.metadata.clientId}` } : null,
  SKILL_UPDATE: (i) =>
    i.metadata?.clientId ? { path: `/admin/skill-assessment/edit/${i.metadata.clientId}` } : null,
  // settlementReview marks the accounts team's copy of a withdrawal: their job is the money, not
  // the application form, so they go to the account editor with the settlement open. Everyone
  // else's copy of the same event still opens the application.
  ADMISSION_UPDATE: (i) =>
    i.metadata?.settlementReview
      ? directAccountEditor(i, { openWithdrawalSettlement: true })
      : i.metadata?.admissionId
      ? {
          path: `/admin/admission/edit/${i.metadata.admissionId}`,
          state: { tab: i.metadata.tab ?? 2 },
        }
      : clientView(i.metadata?.clientId, 4),
  ACCOUNT_ADMIN_NOTIFY: (i) => clientAccountsTab(i.metadata?.clientId),
  CLIENT_UPDATE: (i) => clientView(i.metadata?.clientId, 3),
  WHATSAPP_MESSAGE: (i) => clientView(i.metadata?.clientId),
  // documentRequestId lets ClientDocumentsTab preselect the request the notification is about,
  // rather than landing on the tab with nothing open.
  CLIENT_DOCUMENTS_STARTED: (i) =>
    i.metadata?.clientId
      ? {
          path: `/admin/client/view/${i.metadata.clientId}`,
          state: { tab: "documents", documentRequestId: i.metadata?.requestId ?? null },
        }
      : null,
  CLIENT_DOCUMENTS_SUBMITTED: (i) =>
    i.metadata?.clientId
      ? {
          path: `/admin/client/view/${i.metadata.clientId}`,
          state: { tab: "documents", documentRequestId: i.metadata?.requestId ?? null },
        }
      : null,
  GENERIC: (i) =>
    i.metadata?.clientId ? { path: `/admin/client/view/${i.metadata.clientId}` } : null,

  // Referral partner portal. Partner-bound types must resolve to a /partner route unconditionally:
  // returning null hands the item to fallback(), which sends it to /admin/client/view/{clientId},
  // and a partner following that link bounces off the admin guard.
  REFERRAL_PARTNER_INVITED: (i) => partnerDetail(i.metadata?.partnerId),
  REFERRAL_PARTNER_ONBOARDED: (i) => partnerDetail(i.metadata?.partnerId),
  REFERRAL_LEAD_SUBMITTED: (i) => ({
    path: "/admin/referral/referred-clients",
    state: i.metadata?.leadId ? { leadId: i.metadata.leadId } : undefined,
  }),
  REFERRAL_LEAD_CLIENT_SUBMITTED: () => ({ path: "/partner/leads" }),
  REFERRAL_LEAD_ACTIVATED: () => ({ path: "/partner/leads" }),
  REFERRAL_LEAD_REJECTED: () => ({ path: "/partner/leads" }),
  // A case officer assigned at lead activation opens the client they were given.
  REFERRAL_CASE_OFFICER_ASSIGNED: (i) => clientView(i.metadata?.clientId),
  REFERRAL_DOC_REQUESTED: (i) =>
    i.metadata?.documentRequestId
      ? { path: `/partner/documents/${i.metadata.documentRequestId}` }
      : { path: "/partner/documents" },
  REFERRAL_DOC_SUBMITTED: (i) =>
    i.metadata?.clientId
      ? { path: `/admin/client/view/${i.metadata.clientId}`, state: { tab: "documents" } }
      : null,
  // A request the partner raised themselves lands on the same Documents tab: the office
  // reviews what the partner is collecting even though nobody asked for it.
  REFERRAL_DOC_RAISED: (i) =>
    i.metadata?.clientId
      ? { path: `/admin/client/view/${i.metadata.clientId}`, state: { tab: "documents" } }
      : null,
  REFERRAL_COMMISSION_ACCRUED: () => ({ path: "/partner/payments" }),
  REFERRAL_PAYOUT_PAID: () => ({ path: "/partner/payments" }),
  REFERRAL_PARTNER_MESSAGE: (i) =>
    partnerDetail(i.metadata?.partnerId, {
      tab: "messages",
      clientId: i.metadata?.clientId ?? null,
    }),
  // A message about one client belongs on that client in the portal, not in the general inbox.
  REFERRAL_ADMIN_MESSAGE: (i) =>
    i.metadata?.clientId
      ? { path: `/partner/clients/${i.metadata.clientId}` }
      : { path: "/partner/alerts" },
  REFERRAL_TICKET_OPENED: (i) => officeTicket(i.metadata?.ticketId),
  REFERRAL_TICKET_PARTNER_UPDATE: (i) => officeTicket(i.metadata?.ticketId),
  REFERRAL_APPOINTMENT_BOOKED: (i) => ({
    path: "/admin/appointments",
    state: i.metadata?.appointmentId ? { appointmentId: i.metadata.appointmentId } : undefined,
  }),
  REFERRAL_APPOINTMENT_CANCELLED: (i) => ({
    path: "/admin/appointments",
    state: i.metadata?.appointmentId ? { appointmentId: i.metadata.appointmentId } : undefined,
  }),
  REFERRAL_TICKET_OFFICE_UPDATE: (i) =>
    i.metadata?.ticketId
      ? { path: `/partner/help/tickets/${i.metadata.ticketId}` }
      : { path: "/partner/help" },
  // Offers and terms apply to the partner side of a staff member too, so these land in the portal.
  REFERRAL_PROGRAM_OFFER: (i) =>
    i.metadata?.offerId
      ? { path: `/partner/offers/${i.metadata.offerId}` }
      : { path: "/partner/dashboard" },
  REFERRAL_PROGRAM_TERMS: () => ({ path: "/partner/settings?tab=terms" }),
  REFERRAL_PARTNER_DOC_VERIFIED: () => ({ path: "/partner/settings?tab=business" }),
  REFERRAL_PARTNER_DOC_REJECTED: () => ({ path: "/partner/settings?tab=business" }),
  REFERRAL_PARTNER_DOC_UPLOADED: (i) => partnerDetail(i.metadata?.partnerId),
  // The queue has no per-request route: the notification opens it for review of the new row.
  REFERRAL_APPLICATION_REQUESTED: () => ({ path: "/admin/admin-referral/application-requests" }),
};

function fallback(item) {
  const clientId = item?.metadata?.clientId ?? item?.clientId;
  return clientId ? { path: `/admin/client/view/${clientId}` } : null;
}

/** Resolve `{ path, state }` for a notification by its structured type + metadata. */
export function resolveDestination(item) {
  if (!item) return null;
  const fn = navRegistry[item.type];
  if (fn) return fn(item) ?? fallback(item);
  return fallback(item);
}
